import React, { useState, useMemo, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Settings, Smartphone } from 'lucide-react';
import { useSelectedMetrics, getCpaClassName, getCosClassName, getMetricObjectiveClassName, useComponentMetrics } from '../../utils/metricsUtils';
import MetricsConfigModal from '../MetricsConfigModal';
import ModuleVisibilityToggle from '../ModuleVisibilityToggle';

/**
 * Composant table des appareils Microsoft Ads avec graphiques
 * Optimisé avec React.memo et useMemo pour les performances
 */
const DeviceTable = React.memo(({
  deviceData = [],
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedAccount = null,
  COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8'],
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'msads_devices'
}) => {
  // États locaux pour cette table
  const [showDeviceConversions, setShowDeviceConversions] = useState(true);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // Fonction de tri des colonnes
  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  }, []);

  // Hook pour les métriques sélectionnées (pour les couleurs COS/CPA)
  const selectedMetrics = useSelectedMetrics();

  // Hook pour les métriques du composant (réactif aux changements)
  const componentMetrics = useComponentMetrics('msads_devices', selectedAccount?.id || 'global');

  // Fonction helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Normalisation des noms d'appareils et tri selon sortConfig (memoized)
  const normalizedDeviceData = useMemo(() => {
    if (!deviceData || deviceData.length === 0) return [];

    const normalized = deviceData.map(device => ({
      ...device,
      deviceName: device.device === 'Computer' ? 'Ordinateur' :
                  device.device === 'Smartphone' ? 'Smartphone' :
                  device.device === 'Tablet' ? 'Tablette' : device.device
    }));

    // Tri selon sortConfig
    return normalized.sort((a, b) => {
      let aValue, bValue;
      switch (sortConfig.key) {
        case 'device':
          aValue = a.deviceName || '';
          bValue = b.deviceName || '';
          break;
        case 'ctr':
          aValue = a.impressions > 0 ? (a.clicks / a.impressions * 100) : 0;
          bValue = b.impressions > 0 ? (b.clicks / b.impressions * 100) : 0;
          break;
        case 'cpc':
          aValue = a.clicks > 0 ? (a.cost / a.clicks) : 0;
          bValue = b.clicks > 0 ? (b.cost / b.clicks) : 0;
          break;
        case 'conversionRate':
          aValue = a.clicks > 0 ? (a.conversions / a.clicks * 100) : 0;
          bValue = b.clicks > 0 ? (b.conversions / b.clicks * 100) : 0;
          break;
        case 'cpa':
          aValue = a.conversions > 0 ? (a.cost / a.conversions) : 0;
          bValue = b.conversions > 0 ? (b.cost / b.conversions) : 0;
          break;
        case 'cos':
          aValue = a.conversionsValue > 0 ? (a.cost / a.conversionsValue * 100) : 0;
          bValue = b.conversionsValue > 0 ? (b.cost / b.conversionsValue * 100) : 0;
          break;
        case 'roas':
          aValue = a.cost > 0 ? (a.conversionsValue / a.cost * 100) : 0;
          bValue = b.cost > 0 ? (b.conversionsValue / b.cost * 100) : 0;
          break;
        case 'roi':
          aValue = (a.cost > 0 && a.conversionsValue > 0) ? ((a.conversionsValue - a.cost) / a.cost) : 0;
          bValue = (b.cost > 0 && b.conversionsValue > 0) ? ((b.conversionsValue - b.cost) / b.cost) : 0;
          break;
        default:
          aValue = a[sortConfig.key] || 0;
          bValue = b[sortConfig.key] || 0;
      }

      if (typeof aValue === 'string') {
        return sortConfig.direction === 'desc'
          ? bValue.localeCompare(aValue)
          : aValue.localeCompare(bValue);
      }
      return sortConfig.direction === 'desc' ? bValue - aValue : aValue - bValue;
    });
  }, [deviceData, sortConfig]);

  // Calcul des totaux (memoized)
  const totals = useMemo(() => {
    if (!normalizedDeviceData.length) return {};

    const baseTotals = normalizedDeviceData.reduce((acc, device) => ({
      impressions: acc.impressions + (device.impressions || 0),
      clicks: acc.clicks + (device.clicks || 0),
      cost: acc.cost + (device.cost || 0),
      conversions: acc.conversions + (device.conversions || 0),
      conversionsValue: acc.conversionsValue + (device.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [normalizedDeviceData]);

  // Données pour le graphique des clics (memoized)
  const clicksChartData = useMemo(() => {
    if (!normalizedDeviceData.length) return [];
    
    return normalizedDeviceData
      .map(device => ({
        name: device.deviceName,
        value: device.clicks || 0,
        percentage: totals.clicks > 0 ? ((device.clicks / totals.clicks) * 100).toFixed(1) : 0
      }))
      .filter(device => parseFloat(device.percentage) > 0.0);
  }, [normalizedDeviceData, totals.clicks]);

  // Données pour le graphique des conversions (memoized)
  const conversionsChartData = useMemo(() => {
    if (!normalizedDeviceData.length) return [];
    
    return normalizedDeviceData
      .map(device => ({
        name: device.deviceName,
        value: device.conversions || 0,
        percentage: totals.conversions > 0 ? ((device.conversions / totals.conversions) * 100).toFixed(1) : 0
      }))
      .filter(device => parseFloat(device.percentage) > 0.0);
  }, [normalizedDeviceData, totals.conversions]);

  // Données pour la légende (memoized)
  const legendData = useMemo(() => {
    if (!normalizedDeviceData.length) return [];
    
    return normalizedDeviceData.map((device, index) => ({
      value: device.deviceName,
      type: 'rect',
      color: COLORS[index % COLORS.length]
    }));
  }, [normalizedDeviceData, COLORS]);

  // Ne pas rendre si pas de données
  if (!normalizedDeviceData.length) return null;

  // Vérification de visibilité pour les clients
  const clientModuleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && !clientModuleVisible) {
    return null;
  }

  return (
    <div className="device-conversions-section">
      <div className="section-header">
        <h4 style={{ fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}><Smartphone size={18} /> Appareils</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={clientModuleVisible}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Appareils Microsoft Ads"
            />
          ) : (
            <button
              onClick={() => setShowDeviceConversions(!showDeviceConversions)}
              className="section-toggle-btn"
              title={showDeviceConversions ? "Masquer la section" : "Afficher la section"}
            >
              {showDeviceConversions ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {(userRole !== 'CLIENT' || clientModuleVisible) && showDeviceConversions && (
        <>
          {/* Graphiques en secteurs côte à côte */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {/* Pie chart Clics (gauche) */}
              <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Clics par appareil</h5>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={clicksChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percentage }) => `${percentage}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {clicksChartData.map((entry, index) => (
                            <Cell key={`clicks-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value + ' clics', name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* Légende commune (centre) */}
                  <div style={{ 
                    minWidth: '120px', 
                    padding: '0 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    {legendData.map((item, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        marginBottom: '8px',
                        fontSize: '13px'
                      }}>
                        <div style={{ 
                          width: '12px', 
                          height: '12px', 
                          backgroundColor: item.color, 
                          marginRight: '8px',
                          borderRadius: '2px'
                        }}></div>
                        <span>{item.value}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pie chart Conversions (droite) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Conversions par appareil</h5>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={conversionsChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percentage }) => `${percentage}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {conversionsChartData.map((entry, index) => (
                            <Cell key={`conversions-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value + ' conversions', name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

          {/* Tableau détaillé des appareils */}
          <div className="device-table-wrapper">
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('device')} style={{ cursor: 'pointer' }}>
                    Appareil {sortConfig.key === 'device' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {isComponentMetricActive('impressions') && (
                    <th className="sortable numeric" onClick={() => handleSort('impressions')} style={{ cursor: 'pointer' }}>
                      Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('ctr') && (
                    <th className="sortable numeric" onClick={() => handleSort('ctr')} style={{ cursor: 'pointer' }}>
                      CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('clics') && (
                    <th className="sortable numeric" onClick={() => handleSort('clicks')} style={{ cursor: 'pointer' }}>
                      Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cpc') && (
                    <th className="sortable numeric" onClick={() => handleSort('cpc')} style={{ cursor: 'pointer' }}>
                      CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cout') && (
                    <th className="sortable numeric" onClick={() => handleSort('cost')} style={{ cursor: 'pointer' }}>
                      Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('taux_conv') && (
                    <th className="sortable numeric" onClick={() => handleSort('conversionRate')} style={{ cursor: 'pointer' }}>
                      Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('conv') && (
                    <th className="sortable numeric" onClick={() => handleSort('conversions')} style={{ cursor: 'pointer' }}>
                      Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cpa') && (
                    <th className="sortable numeric" onClick={() => handleSort('cpa')} style={{ cursor: 'pointer' }}>
                      CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('valeur_conv') && (
                    <th className="sortable numeric" onClick={() => handleSort('conversionsValue')} style={{ cursor: 'pointer' }}>
                      Valeur Conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cos') && (
                    <th className="sortable numeric" onClick={() => handleSort('cos')} style={{ cursor: 'pointer' }}>
                      COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('roas') && (
                    <th className="sortable numeric" onClick={() => handleSort('roas')} style={{ cursor: 'pointer' }}>
                      ROAS {sortConfig.key === 'roas' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('roi') && (
                    <th className="sortable numeric" onClick={() => handleSort('roi')} style={{ cursor: 'pointer' }}>
                      ROI {sortConfig.key === 'roi' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {normalizedDeviceData.map((device, index) => (
                  <tr key={index}>
                    <td className="device-name">
                      <span
                        className="device-indicator"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></span>
                      {device.deviceName}
                    </td>
                    {isComponentMetricActive('impressions') && (
                      <td className="numeric">{formatNumber(device.impressions || 0)}</td>
                    )}
                    {isComponentMetricActive('ctr') && (
                      <td className="numeric">{formatNumber(device.ctr || 0, 2)}%</td>
                    )}
                    {isComponentMetricActive('clics') && (
                      <td className="numeric">{formatNumber(device.clicks || 0)}</td>
                    )}
                    {isComponentMetricActive('cpc') && (
                      <td className="numeric">€{formatNumber(device.cpc || 0, 2)}</td>
                    )}
                    {isComponentMetricActive('cout') && (
                      <td className="numeric">€{formatNumber(device.cost || 0, 2)}</td>
                    )}
                    {isComponentMetricActive('taux_conv') && (
                      <td className="numeric">{formatNumber(device.conversionRate || 0, 2)}%</td>
                    )}
                    {isComponentMetricActive('conv') && (
                      <td className="numeric">{formatNumber(device.conversions || 0)}</td>
                    )}
                    {isComponentMetricActive('cpa') && (
                      <td className={`numeric ${getCpaClassName(device.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                        {device.cpa >= 0 ? `€${formatNumber(device.cpa, 2)}` : '-'}
                      </td>
                    )}
                    {isComponentMetricActive('valeur_conv') && (
                      <td className="numeric">€{formatNumber(device.conversionsValue || 0, 2)}</td>
                    )}
                    {isComponentMetricActive('cos') && (
                      <td className={`numeric ${getCosClassName((device.cost || 0) / (device.conversionsValue || 1) * 100, selectedAccount?.id || 'global', moduleId)}`}>
                        {device.conversionsValue > 0 ? formatNumber((device.cost || 0) / (device.conversionsValue || 1) * 100, 1) + '%' : formatNumber(0, 1) + '%'}
                      </td>
                    )}
                    {isComponentMetricActive('roas') && (() => {
                      const roas = device.cost > 0 ? (device.conversionsValue / device.cost) * 100 : 0;
                      return (
                        <td className={`numeric ${getMetricObjectiveClassName('roas', roas, moduleId, selectedAccount?.id || 'global')}`}>
                          {roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}
                        </td>
                      );
                    })()}
                    {isComponentMetricActive('roi') && (() => {
                      const hasData = device.cost > 0 && device.conversionsValue > 0;
                      const roi = hasData ? (device.conversionsValue - device.cost) / device.cost : 0;
                      return (
                        <td className={`numeric ${getMetricObjectiveClassName('roi', roi, moduleId, selectedAccount?.id || 'global')}`}>
                          {formatNumber(roi, 2)}
                        </td>
                      );
                    })()}
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr className="totals-row">
                  <td><strong>TOTAL</strong></td>
                  {isComponentMetricActive('impressions') && (
                    <td className="numeric"><strong>{formatNumber(totals.impressions)}</strong></td>
                  )}
                  {isComponentMetricActive('ctr') && (
                    <td className="numeric"><strong>{formatNumber(totals.ctr, 2)}%</strong></td>
                  )}
                  {isComponentMetricActive('clics') && (
                    <td className="numeric"><strong>{formatNumber(totals.clicks)}</strong></td>
                  )}
                  {isComponentMetricActive('cpc') && (
                    <td className="numeric"><strong>€{formatNumber(totals.cpc, 2)}</strong></td>
                  )}
                  {isComponentMetricActive('cout') && (
                    <td className="numeric"><strong>€{formatNumber(totals.cost, 2)}</strong></td>
                  )}
                  {isComponentMetricActive('taux_conv') && (
                    <td className="numeric"><strong>{formatNumber(totals.conversionRate, 2)}%</strong></td>
                  )}
                  {isComponentMetricActive('conv') && (
                    <td className="numeric"><strong>{formatNumber(totals.conversions)}</strong></td>
                  )}
                  {isComponentMetricActive('cpa') && (
                    <td className={`numeric ${getCpaClassName(totals.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                      <strong>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</strong>
                    </td>
                  )}
                  {isComponentMetricActive('valeur_conv') && (
                    <td className="numeric"><strong>€{formatNumber(totals.conversionsValue, 2)}</strong></td>
                  )}
                  {isComponentMetricActive('cos') && (
                    <td className={`numeric ${getCosClassName(totals.cos || 0, selectedAccount?.id || 'global', moduleId)}`}>
                      <strong>{formatNumber(totals.cos, 1)}%</strong>
                    </td>
                  )}
                  {isComponentMetricActive('roas') && (() => {
                    const roas = totals.cost > 0 ? (totals.conversionsValue / totals.cost) * 100 : 0;
                    return (
                      <td className={`numeric ${getMetricObjectiveClassName('roas', roas, moduleId, selectedAccount?.id || 'global')}`}>
                        <strong>{roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}</strong>
                      </td>
                    );
                  })()}
                  {isComponentMetricActive('roi') && (() => {
                    const hasData = totals.cost > 0 && totals.conversionsValue > 0;
                    const roi = hasData ? (totals.conversionsValue - totals.cost) / totals.cost : 0;
                    return (
                      <td className={`numeric ${getMetricObjectiveClassName('roi', roi, moduleId, selectedAccount?.id || 'global')}`}>
                        <strong>{formatNumber(roi, 2)}</strong>
                      </td>
                    );
                  })()}
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {/* Modal de configuration des métriques */}
      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId="msads_devices"
          accountId={selectedAccount?.id || 'global'}
          title="Appareils Microsoft Ads"
          hideApplyToAll={true}
        />
      )}
    </div>
  );
});

DeviceTable.displayName = 'DeviceTable';

export default DeviceTable;